import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import CommonLayout from "../../layouts/commonLayout/CommonLayout";
import { NavLink, Outlet, useParams, useSearchParams } from "react-router-dom";
import baseUrl from "../../api/baseUrl";
import PatientModal from "./PatientModal";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicationIcon from "@mui/icons-material/Medication";
import HistoryIcon from "@mui/icons-material/History";
import EventNoteIcon from "@mui/icons-material/EventNote";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaymentIcon from "@mui/icons-material/Payment";
import ScienceIcon from "@mui/icons-material/Science";
import DescriptionIcon from "@mui/icons-material/Description";
import getDecryptedToken from "../../api/getDecryptedToken";
import type { PatientTokenInfo } from "../patient/components/ModalHeader";
import TreatmentPatientHeader from "./TreatmentPatientHeader";
import FullPatientEditModal from "../patient/components/FullPatientEditModal";
import type { AddTreatmentOutletContext } from "./addTreatmentOutletContext";
import ReportModal from "./treatmenttable/components/reportModal/ReportModal";
import { readAppointmentIdFromSearch, withAppointmentIdQuery } from "../../utils/appointmentLinkContext";

function AddTreatment() {
  const [data, setdata] = useState<any>(null);
  const [patientId, setPatientId] = useState<string>("");
  const [tokenInfo, setTokenInfo] = useState<PatientTokenInfo | null>(null);
  const [close, setclose] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [hasPendingInput, setHasPendingInput] = useState(false);
  const { ID } = useParams();
  const [searchParams] = useSearchParams();
  const appointmentId = readAppointmentIdFromSearch(searchParams);
  const labsFeatureEnabled = getDecryptedToken("featureLabs") === "true";

  const outletContext = useMemo<AddTreatmentOutletContext>(
    () => ({ setHasPendingInput }),
    []
  );

  const handleNavClick = useCallback(
    (e: React.MouseEvent) => {
      if (!hasPendingInput) return;
      e.preventDefault();
      alert("Please save or remove pending inputs before leaving");
    },
    [hasPendingInput]
  );

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as "light" | "dark" | null;
    const htmlTheme = document.documentElement.getAttribute("data-theme") as
      | "light"
      | "dark"
      | null;
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (htmlTheme) {
      setTheme(htmlTheme);
    }

    const observer = new MutationObserver(() => {
      const currentTheme = document.documentElement.getAttribute("data-theme") as
        | "light"
        | "dark"
        | null;
      if (currentTheme) setTheme(currentTheme);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  const fetchTreatment = useCallback(() => {
    if (!ID) return;
    baseUrl
      .get("treatment/find-treatment", {
        headers: { treatmentid: ID },
      })
      .then((response) => {
        const patient = response.data.data.patientId;
        setdata(patient);
        const pid =
          patient?._id != null
            ? String(patient._id)
            : typeof patient === "string"
              ? patient
              : "";
        setPatientId(pid);
      })
      .catch((error) => console.log(error));
  }, [ID]);

  useEffect(() => {
    fetchTreatment();
  }, [fetchTreatment]);

  const handleEditDetails = useCallback((_id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditOpen(true);
  }, []);

  const handlePatientSaved = useCallback(async () => {
    if (!patientId) return;
    try {
      const res = await baseUrl.get("patient/findById", {
        headers: { patientid: patientId },
      });
      setdata(res.data?.data ?? null);
    } catch (err) {
      console.error(err);
    }
  }, [patientId]);

  useEffect(() => {
    if (!patientId) {
      setTokenInfo(null);
      return;
    }
    baseUrl
      .get("/appointment/findPatientTokenToday", {
        headers: { patientid: patientId },
      })
      .then((res) => {
        const token = res.data?.data;
        if (token?.tokenNo != null && Number(token.tokenNo) > 0) {
          setTokenInfo({
            tokenNo: token.tokenNo,
            queueGeneratedAt: token.queueGeneratedAt ?? null,
          });
        } else {
          setTokenInfo(null);
        }
      })
      .catch(() => setTokenInfo(null));
  }, [patientId]);

  const handleCloseModal = () => setclose(false);
  const handleViewPatientClick = () => setclose(true);
  const handleReportClick = useCallback(() => {
    if (hasPendingInput) {
      alert("Please save or remove pending inputs before generating the report");
      return;
    }
    setReportOpen(true);
  }, [hasPendingInput]);

  const navItems = [
    { path: `diagnose/${ID}`, label: "DIAGNOSE", icon: LocalHospitalIcon },
    { path: `treatment-plan/${ID}`, label: "TREATMENT PLAN", icon: EventNoteIcon },
    { path: `treatment-table/${ID}`, label: "TREATMENT", icon: MedicationIcon },
    ...(labsFeatureEnabled
      ? [{ path: `lab-order/${ID}`, label: "LAB ORDER", icon: ScienceIcon }]
      : []),
    { path: `drugs/${ID}`, label: "DRUGS", icon: MedicationIcon },
    { path: `past-treatment/${ID}`, label: "PAST TREATMENT", icon: HistoryIcon },
    { path: `treatment-planned/${ID}`, label: "PLANNED", icon: EventNoteIcon },
    { path: `past-invoice/${ID}`, label: "PAYMENT", icon: PaymentIcon },
  ];

  return (
    <CommonLayout>
      <div className="mb-2.5">
        {/* Patient Profile Header */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden mb-2.5">
          <TreatmentPatientHeader
            patientData={data}
            theme={theme}
            onEdit={handleEditDetails}
            tokenInfo={tokenInfo}
            extraActions={
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleReportClick}
                  className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300 dark:hover:bg-amber-900/30"
                >
                  <DescriptionIcon sx={{ fontSize: 14 }} />
                  <span className="hidden sm:inline">Generate Report</span>
                </button>
                <button
                  type="button"
                  onClick={handleViewPatientClick}
                  className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-gradient-to-r from-primary to-primaryLight px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:opacity-95"
                >
                  <VisibilityIcon sx={{ fontSize: 14 }} />
                  <span className="hidden sm:inline">Patient Details</span>
                </button>
              </div>
            }
          />
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-100 dark:border-slate-700 p-1 flex flex-wrap gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={withAppointmentIdQuery(item.path, appointmentId)}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  isActive
                    ? "bg-gradient-to-r from-primary to-primaryLight text-white shadow-lg shadow-primary/20"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                }`
              }
            >
              <item.icon sx={{ fontSize: 14 }} />
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Content Outlet */}
      <div className="w-full min-w-0">
        <Suspense
          fallback={
            <div className="fixed inset-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          }
        >
          <Outlet context={outletContext} />
        </Suspense>
      </div>

      {close && <PatientModal patient={data} onClose={handleCloseModal} />}

      <FullPatientEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        patientId={patientId}
        theme={theme}
        onSaved={handlePatientSaved}
      />
      {reportOpen && <ReportModal setReportOpen={setReportOpen} />}
    </CommonLayout>
  );
}

export default AddTreatment;
